import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Hero } from "@/components/site/Hero";
import { SearchBar } from "@/components/site/SearchBar";
import { PropertyCard } from "@/components/site/PropertyCard";
import { Reveal, MaskLine, Eyebrow } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { InquiryForm } from "@/components/site/InquiryForm";
import { useLang } from "@/lib/i18n";
import { api, useSite, mediaUrl, useContent } from "@/lib/site";
import { WaButton } from "@/components/site/WhatsApp";

const Featured = () => {
  const { t } = useLang();
  const { txt } = useContent();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/properties?featured=true").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  return (
    <section className="bg-ivory py-24 lg:py-32" data-testid="featured-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <Eyebrow className="text-midnight/70 mb-5">{t.featured}</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-midnight leading-[1.05]"><MaskLine>{txt("slogan")}</MaskLine></h2>
          </div>
          <Link to="/nehnutelnosti" data-testid="featured-view-all" className="link-underline text-[11px] tracking-[0.3em] uppercase text-midnight flex items-center gap-3 shrink-0">{t.viewAll} <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.slice(0, 6).map((p, i) => <PropertyCard key={p.id} p={p} i={i} />)}
        </div>
      </div>
    </section>
  );
};

const Lifestyle = () => {
  const { t } = useLang();
  const { txt, img } = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="grid grid-cols-1 lg:grid-cols-2 bg-midnight" data-testid="lifestyle-section">
      <div className="relative overflow-hidden min-h-[560px] lg:min-h-[720px]">
        <motion.img style={{ y, scale: 1.25 }} src={img("lifestyle_image", "/img/yacht.jpg")} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-midnight via-midnight/70 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-24">
          <Eyebrow className="text-ivory/80 mb-8">{t.lifestyleEyebrow}</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.08]">
            <MaskLine>{txt("lifestyleTitle1")}</MaskLine>
            <MaskLine delay={0.15} className="italic text-gold">{txt("lifestyleTitle2")}</MaskLine>
          </h2>
          <Reveal delay={0.3}><p className="mt-8 text-ivory/80 max-w-md leading-relaxed text-sm sm:text-base">{txt("lifestyleText")}</p></Reveal>
          <Reveal delay={0.45}><Link to="/o-nas" data-testid="lifestyle-about-btn" className="btn-gold mt-10 inline-flex">{t.aboutBtn} <ArrowRight size={14} /></Link></Reveal>
        </div>
      </div>
      <div className="relative overflow-hidden min-h-[420px] lg:min-h-[720px]">
        <motion.div initial={{ clipPath: "inset(12% 12% 12% 12%)" }} whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }} viewport={{ once: true, amount: 0.05 }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0">
          <img src={img("split_image", "/img/p1.jpg")} alt="" className="w-full h-full object-cover" />
        </motion.div>
      </div>
    </section>
  );
};

const Marquee = () => {
  const { t } = useLang();
  const row = [...t.marquee, ...t.marquee];
  return (
    <section className="bg-sand/40 py-12 overflow-hidden border-y border-sand" data-testid="marquee-section">
      <div className="marquee flex whitespace-nowrap w-max">
        {[0, 1].map((k) => (
          <div key={k} className="flex shrink-0" aria-hidden={k === 1}>
            {row.map((w, i) => (
              <span key={i} className="font-display italic text-5xl sm:text-7xl text-midnight/85 px-10 flex items-center gap-10">{w}<span className="w-2 h-2 rotate-45 bg-gold inline-block" /></span>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

const Destinations = () => {
  const { t, tr } = useLang();
  const { txt } = useContent();
  const { countries } = useSite();
  const [active, setActive] = useState(0);
  const cur = countries[active];
  return (
    <section className="bg-ivory py-24 lg:py-32" data-testid="destinations-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-5">
          <Eyebrow className="text-midnight/70 mb-5">{t.destEyebrow}</Eyebrow>
          <h2 className="font-display italic text-4xl sm:text-5xl text-midnight leading-[1.1] mb-12"><MaskLine>{txt("destTitle")}</MaskLine></h2>
          <ul>
            {countries.map((c, i) => (
              <li key={c.id}>
                <Link to={`/nehnutelnosti?country=${c.id}`} onMouseEnter={() => setActive(i)} data-testid={`destination-${c.slug}`}
                  className="group flex items-center justify-between py-4 border-b border-sand/70">
                  <span className="flex items-center gap-5">
                    <img src={mediaUrl(c.image)} alt="" className="w-14 h-14 object-cover lg:hidden" />
                    <span className={`font-display text-2xl sm:text-3xl transition-colors duration-500 ${active === i ? "text-gold" : "text-midnight"}`}>{tr(c.name)}</span>
                  </span>
                  <ArrowRight size={16} strokeWidth={1.2} className={`transition-all duration-500 ${active === i ? "text-gold translate-x-0 opacity-100" : "opacity-0 -translate-x-3"}`} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="hidden lg:block lg:col-span-7 relative aspect-[4/5] overflow-hidden">
          {countries.map((c, i) => (
            <motion.img key={c.id} src={mediaUrl(c.image)} alt={tr(c.name)} initial={false} animate={{ opacity: i === active ? 1 : 0, scale: i === active ? 1 : 1.08 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0 w-full h-full object-cover" />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 to-transparent" />
          <div className="absolute bottom-10 left-10 text-ivory">
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold">0{active + 1}</div>
            <div className="font-display text-5xl mt-2">{cur && tr(cur.name)}</div>
            <div className="text-sm text-ivory/80 mt-2">{cur?.regions?.map((r) => tr(r.name)).join(" · ")}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Reels = () => {
  const { t, tr } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/properties").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setItems(list.flatMap((p) => (p.reels || []).map((u) => ({ u, p }))).slice(0, 8));
    }).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <section className="bg-midnight py-24 overflow-hidden" data-testid="reels-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Eyebrow className="text-gold mb-5">{t.reelsEyebrow}</Eyebrow>
        <h2 className="font-display italic text-4xl sm:text-5xl text-ivory mb-12"><MaskLine>{t.reelsTitle}</MaskLine></h2>
        <div className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6" data-lenis-prevent-wheel>
          {items.map(({ u, p }, i) => (
            <motion.div key={u + i} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.8, delay: i * 0.08 }} className="snap-start shrink-0 w-[62vw] sm:w-[260px]">
              <Link to={`/nehnutelnosti/${p.slug}`} className="group block relative aspect-[9/16] overflow-hidden bg-black" data-testid={`reel-${i}`}>
                <video src={mediaUrl(u)} autoPlay muted loop playsInline className="w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-midnight/90 to-transparent">
                  <div className="font-display text-lg text-ivory leading-tight">{tr(p.title)}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PartnerTeaser = () => {
  const { t } = useLang();
  const { txt, img } = useContent();
  return (
    <section className="bg-mist/60 py-20 lg:py-28" data-testid="partner-teaser">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 aspect-[4/3] overflow-hidden">
          <motion.img initial={{ scale: 1.2 }} whileInView={{ scale: 1 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 1.6 }} src={img("partner_image", "/img/p2.jpg")} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="lg:col-span-7">
          <Eyebrow className="text-midnight/70 mb-5">{t.partner.eyebrow}</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl text-midnight"><MaskLine>{txt("partnerTitle") || t.partner.title}</MaskLine></h2>
          <Reveal delay={0.15}><p className="mt-6 text-midnight/80 max-w-xl">{t.partner.teaser}</p></Reveal>
          <Reveal delay={0.3} className="mt-8 flex flex-wrap gap-3">
            <Link to="/spolupraca" className="btn-ghost-dark" data-testid="partner-teaser-more">{t.partner.more} <ArrowRight size={14} className="ms-2" /></Link>
            <WaButton testid="partner-teaser-wa" text={`${t.partner.title} – MEVALUX PROPERTY`} label={t.partner.cta} />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Cta = () => {
  const { t } = useLang();
  const { txt, img } = useContent();
  return (
    <section className="relative bg-midnight overflow-hidden" data-testid="cta-section">
      <img src={img("cta_image", "/img/sail.jpg")} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-36 grid lg:grid-cols-2 gap-16">
        <div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.08]"><MaskLine>{txt("ctaTitle")}</MaskLine></h2>
          <Reveal delay={0.2}><p className="mt-8 text-ivory/80 max-w-md">{txt("ctaText")}</p></Reveal>
          <Reveal delay={0.3} className="mt-8"><WaButton testid="cta-whatsapp" text="MEVALUX PROPERTY" label="WhatsApp" /></Reveal>
        </div>
        <Reveal delay={0.2}><InquiryForm dark /></Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <main data-testid="home-page">
      <Seo path="/" />
      <Hero />
      <section className="bg-ivory relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 lg:-mt-16 relative">
          <Reveal className="bg-ivory lg:shadow-[0_30px_80px_-30px_rgba(16,42,58,0.35)] lg:p-10"><SearchBar /></Reveal>
        </div>
      </section>
      <Featured />
      <Lifestyle />
      <Reels />
      <Marquee />
      <Destinations />
      <PartnerTeaser />
      <Cta />
    </main>
  );
}
